import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { OverallMetrics, MetroPerformance, PaymentMethodBreakdown, PaymentJob } from '@/types/reports';
import { startOfDayInTz, endOfDayInTz, DEFAULT_TIMEZONE, formatDateInputInTz } from '@/utils/timezone';
import { calculateConversionRate, calculateCompletionRate } from '@/utils/dateFieldStandards';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';
import { netFromGross, taxFromGross, grossFromNet } from '@/utils/tax';
import { fetchAllRows, fetchAllRowsIn } from '@/utils/fetchAllRows';

export const useOverallReport = (startDate: Date, endDate: Date, timezone: string = DEFAULT_TIMEZONE) => {
  const tax = useCompanyTax();
  const { canSeeCompanyFinancials } = useVisibility();
  return useQuery({
    enabled: canSeeCompanyFinancials,
    queryKey: ['reports', 'overall-report', startDate, endDate, timezone, tax.rate, tax.enabled],
    queryFn: async (): Promise<OverallMetrics> => {
      // Use configured timezone for date boundaries
      const start = startOfDayInTz(startDate, timezone).toISOString();
      const end = endOfDayInTz(endDate, timezone).toISOString();

      // Use job_date (editable business date) for all revenue filtering
      const startDateStr = formatDateInputInTz(startDate, timezone);
      const endDateStr = formatDateInputInTz(endDate, timezone);
      const startISO = startOfDayInTz(startDate, timezone).toISOString();
      const endISO = endOfDayInTz(endDate, timezone).toISOString();

      // Fetch completed jobs in date range using job_date
      const completedJobs = await fetchAllRows((from, to) => supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          customer_name,
          job_date,
          invoice_total,
          amount_paid,
          toll_cost,
          tech_commission_amount,
          company_profit,
          total_override_commission,
          total_parts_cost,
          total_card_fees,
          revenue_before_gst,
          metro_id
        `)
        .eq('status', 'completed')
        .gte('job_date', startDateStr)
        .lte('job_date', endDateStr)
        .order('id')
        .range(from, to));

      // Fetch total override commissions paid out in this period via linked job's job_date.
      const overrideData = await fetchAllRows((from, to) => supabase
        .from('job_commission_overrides')
        .select('override_amount')
        .gte('job_completion_date', startDateStr) // Keeping as date string for now if this table uses date type
        .lte('job_completion_date', endDateStr)
        .order('id')
        .range(from, to));

      // Get ALL leads in date range with their status for accurate conversion calculation
      const leadsInRange = await fetchAllRows((from, to) => supabase
        .from('leads')
        .select('id, job_id, status, metro_id')
        .gte('created_at', start)
        .lte('created_at', end)
        .order('id')
        .range(from, to));

      // Count total jobs in date range (all statuses) - use scheduled_date for non-closed jobs
      const { count: totalJobsCount, error: totalJobsError } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .gte('scheduled_date', start)
        .lte('scheduled_date', end);

      if (totalJobsError) throw totalJobsError;

      // Count cancelled jobs for completion rate - use cancelled_at timestamp
      const { count: cancelledCount, error: cancelledError } = await supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'cancelled')
        .gte('cancelled_at', startISO)
        .lte('cancelled_at', endISO);

      if (cancelledError) throw cancelledError;

      // Fetch campaign daily spend for date range with metro info
      // spend_date is stored as YYYY-MM-DD in the timezone context
      const dailySpend = await fetchAllRows((from, to) => supabase
        .from('campaign_daily_spend')
        .select(`
          actual_cost,
          campaign:campaigns!inner(metro_id)
        `)
        .gte('spend_date', startDateStr)
        .lte('spend_date', endDateStr)
        .order('id')
        .range(from, to));

      // Group campaign spend by metro_id (gross of tax for like-for-like with revenue).
      const spendByMetro = new Map<string, number>();
      (dailySpend || []).forEach((spend: { actual_cost: number; campaign: { metro_id: string | null } }) => {
        const metroId = spend.campaign?.metro_id;
        if (metroId) {
          spendByMetro.set(metroId, (spendByMetro.get(metroId) || 0) + grossFromNet(spend.actual_cost || 0, tax));
        }
      });

      // Fetch metros for name lookup
      const { data: metros, error: metrosError } = await supabase
        .from('metros')
        .select('id, name');

      if (metrosError) throw metrosError;

      const metroNameMap = new Map(metros?.map(m => [m.id, m.name]) || []);

      // Calculate metrics
      const jobs = completedJobs || [];
      const jobsCompleted = jobs.length;

      // Fetch payments for completed jobs
      const completedJobIds = jobs.map(j => j.id);
      const payments = await fetchAllRowsIn(completedJobIds, (ids, from, to) => supabase
        .from('job_payments')
        .select('job_id, amount, payment_method')
        .in('job_id', ids)
        .order('id')
        .range(from, to));

      // Aggregate payments by method (cash, card, bank_transfer, square, other)
      const paymentMethodBreakdown: PaymentMethodBreakdown = {
        cash: 0,
        card: 0,
        bank_transfer: 0,
        other: 0,
        square: 0,
      };

      payments.forEach(payment => {
        if (payment.payment_method === 'cash') {
          paymentMethodBreakdown.cash += payment.amount;
        } else if (payment.payment_method === 'card') {
          paymentMethodBreakdown.card += payment.amount;
        } else if (payment.payment_method === 'bank_transfer') {
          paymentMethodBreakdown.bank_transfer += payment.amount;
        } else if (payment.payment_method === 'square') {
          paymentMethodBreakdown.square += payment.amount;
        } else {
          paymentMethodBreakdown.other += payment.amount;
        }
      });

      // Calculate Total Invoiced (pure invoice_total of completed jobs)
      const totalInvoiced = jobs.reduce((sum, job) => sum + (job.invoice_total || 0), 0);

      // Collected Turnover = sum of all real payments received.
      const totalTurnover =
        paymentMethodBreakdown.cash +
        paymentMethodBreakdown.card +
        paymentMethodBreakdown.bank_transfer +
        paymentMethodBreakdown.square +
        paymentMethodBreakdown.other;

      // Build Other payment jobs list (for drilldown sheet)
      const otherJobs: PaymentJob[] = [];
      const otherPaymentsByJob = new Map<string, number>();

      payments.forEach(p => {
        if (
          p.payment_method !== 'cash' &&
          p.payment_method !== 'card' &&
          p.payment_method !== 'bank_transfer' &&
          p.payment_method !== 'square'
        ) {
          otherPaymentsByJob.set(p.job_id, (otherPaymentsByJob.get(p.job_id) || 0) + p.amount);
        }
      });

      jobs.forEach(job => {
        const otherAmount = otherPaymentsByJob.get(job.id);
        if (otherAmount && otherAmount > 0) {
          otherJobs.push({
            jobId: job.id,
            jobNumber: job.job_number,
            customerName: job.customer_name,
            amount: otherAmount,
            closedDate: job.job_date
          });
        }
      });

      // Turnover after tax: collected revenue ex-tax.
      const turnoverAfterTax = netFromGross(totalTurnover, tax);
      const gstOwed = taxFromGross(totalTurnover, tax);

      const techPay = jobs.reduce((sum, job) => sum + (job.tech_commission_amount || 0), 0);
      const partsCost = jobs.reduce((sum, job) => sum + (job.total_parts_cost || 0), 0);
      const tollCost = jobs.reduce((sum, job) => sum + (job.toll_cost || 0), 0);
      const cardProcessingFees = jobs.reduce((sum, job) => sum + ((job as any).total_card_fees || 0), 0);
      const campaignCostExGst = (dailySpend || []).reduce((sum, spend: { actual_cost: number }) => sum + (spend.actual_cost || 0), 0);
      const campaignCost = grossFromNet(campaignCostExGst, tax);

      // Total managers commission (overrides paid out)
      const managersCommission = (overrideData || []).reduce((sum, o) => sum + (o.override_amount || 0), 0);

      // Calculate Net Company Profit using the exact same math as the P&L to ensure 100% sync
      const grossProfit = turnoverAfterTax - partsCost - tollCost - cardProcessingFees - techPay - managersCommission;

      // Net company profit includes campaign costs as a period expense
      const netCompanyProfit = grossProfit - campaignCost;
      const avgJobProfit = jobsCompleted > 0 ? grossProfit / jobsCompleted : 0;

      const totalLeads = leadsInRange?.length || 0;
      const convertedLeads = leadsInRange?.filter(l => l.job_id !== null).length || 0;
      const lostLeads = leadsInRange?.filter(l => l.status === 'lost').length || 0;

      const leadToJobConversionRate = calculateConversionRate(convertedLeads, totalLeads);
      const jobCompletionRate = calculateCompletionRate(jobsCompleted, totalJobsCount || 0);

      // Calculate metro performance
      const metroMap = new Map<string, MetroPerformance>();

      // Add leads to metros
      (leadsInRange || []).forEach(lead => {
        const metroId = lead.metro_id || 'unknown';
        if (!metroMap.has(metroId)) {
          metroMap.set(metroId, {
            metro_id: metroId,
            metro_name: metroId === 'unknown' ? 'Unknown' : (metroNameMap.get(metroId) || 'Unknown'),
            leads_count: 0,
            jobs_count: 0,
            turnover: 0,
            campaign_cost: 0,
            net_profit: 0,
          });
        }
        const metro = metroMap.get(metroId)!;
        metro.leads_count++;
      });

      // Add jobs to metros (calculate gross profit per metro)
      const grossProfitByMetro = new Map<string, number>();
      jobs.forEach(job => {
        const metroId = job.metro_id || 'unknown';
        if (!metroMap.has(metroId)) {
          metroMap.set(metroId, {
            metro_id: metroId,
            metro_name: metroId === 'unknown' ? 'Unknown' : (metroNameMap.get(metroId) || 'Unknown'),
            leads_count: 0,
            jobs_count: 0,
            turnover: 0,
            campaign_cost: 0,
            net_profit: 0,
          });
        }
        const metro = metroMap.get(metroId)!;
        metro.jobs_count++;
        metro.turnover += job.invoice_total || 0;

        // Track gross profit before campaign costs
        const jobGrossProfit = (job.company_profit || 0) - (job.total_override_commission || 0);
        grossProfitByMetro.set(metroId, (grossProfitByMetro.get(metroId) || 0) + jobGrossProfit);
      });

      // Apply campaign costs and calculate net profit per metro
      metroMap.forEach((metro, metroId) => {
        const metroCampaignCost = spendByMetro.get(metroId) || 0;
        const metroGrossProfit = grossProfitByMetro.get(metroId) || 0;
        metro.campaign_cost = metroCampaignCost;
        metro.net_profit = metroGrossProfit - metroCampaignCost;
      });

      // Sort by turnover descending, put Unknown last
      const metroPerformance = Array.from(metroMap.values())
        .sort((a, b) => {
          if (a.metro_id === 'unknown') return 1;
          if (b.metro_id === 'unknown') return -1;
          return b.turnover - a.turnover;
        });

      return {
        totalInvoiced,
        totalTurnover,
        gstOwed,
        turnoverAfterTax,
        techPay,
        partsCost,
        tollCost,
        cardProcessingFees,
        campaignCost,
        managersCommission,
        netCompanyProfit,
        avgJobProfit,
        jobsCompleted,
        totalLeads,
        totalJobs: totalJobsCount || 0,
        leadToJobConversionRate,
        jobCompletionRate,
        metroPerformance,
        paymentMethodBreakdown,
        otherJobs
      };
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
};
